import { homeLayoutV2 as HOME_LAYOUT_KEY } from "data/local-storage-keys";
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import type { Layouts } from "react-grid-layout";
import { getLocalStorage, setLocalStorage } from "utils/local-storage";
import {
  DEFAULT_STORED,
  addBlock,
  migrate,
  removeBlock,
  type StoredHomeLayout,
} from "./home-blocks";

interface HomeLayoutContextType {
  /** Committed layout, persisted to localStorage. */
  layout: StoredHomeLayout;
  /** Working copy edited while in edit mode. */
  draft: StoredHomeLayout;
  isEditing: boolean;
  startEdit: () => void;
  cancel: () => void;
  commit: () => void;
  /** Sets the draft back to the default layout (persisted on Save). */
  reset: () => void;
  /** Store the per-breakpoint layouts from RGL's onLayoutChange. */
  setLayouts: (layouts: Layouts) => void;
  /** Remove a block from the draft (moves it to the Add block palette). */
  removeDraftBlock: (id: string) => void;
  /** Re-add a hidden block to the draft. */
  addDraftBlock: (id: string) => void;
}

const HomeLayoutContext = createContext<HomeLayoutContextType | undefined>(
  undefined,
);

function loadLayout(): StoredHomeLayout {
  return migrate(getLocalStorage<unknown>(HOME_LAYOUT_KEY, null));
}

export function HomeLayoutProvider({ children }: { children: ReactNode }) {
  const [layout, setCommitted] = useState<StoredHomeLayout>(loadLayout);
  const [draft, setDraft] = useState<StoredHomeLayout>(layout);
  const [isEditing, setIsEditing] = useState(false);

  const startEdit = useCallback(() => {
    setDraft(layout);
    setIsEditing(true);
  }, [layout]);

  const cancel = useCallback(() => {
    setDraft(layout);
    setIsEditing(false);
  }, [layout]);

  const commit = useCallback(() => {
    setCommitted(draft);
    setLocalStorage(HOME_LAYOUT_KEY, draft);
    setIsEditing(false);
  }, [draft]);

  const reset = useCallback(() => {
    setDraft(DEFAULT_STORED);
  }, []);

  const setLayouts = useCallback((layouts: Layouts) => {
    setDraft((current) => ({ ...current, layouts }));
  }, []);

  const removeDraftBlock = useCallback((id: string) => {
    setDraft((current) => removeBlock(current, id));
  }, []);

  const addDraftBlock = useCallback((id: string) => {
    setDraft((current) => addBlock(current, id));
  }, []);

  return (
    <HomeLayoutContext.Provider
      value={{
        layout,
        draft,
        isEditing,
        startEdit,
        cancel,
        commit,
        reset,
        setLayouts,
        removeDraftBlock,
        addDraftBlock,
      }}
    >
      {children}
    </HomeLayoutContext.Provider>
  );
}

export function useHomeLayout() {
  const ctx = useContext(HomeLayoutContext);
  if (!ctx) {
    throw new Error("useHomeLayout must be used within HomeLayoutProvider");
  }
  return ctx;
}
